import { fCurrency } from '@/utils/formatNumber';

export const COUNTRY_FLAG_BASE_URL = 'https://purecatamphetamine.github.io/country-flag-icons/3x2';
export const DEFAULT_PRODUCT_CURRENCY_CODE = 'BRL';
export const ALL_CURRENCIES_FILTER_VALUE = 'all';
const MARKETPLACE_SALES_THRESHOLD_BY_CURRENCY = {
  BRL: 5000,
  USD: 1000,
};

// `hint` responde a dúvida de quem cria produto em moeda estrangeira: o produtor não sabe se o
// comprador de fora verá o preço em dólar ou na moeda dele (CKTPL-1467). Mora aqui, junto da
// moeda, e não no campo, para a opção nova já nascer com a explicação dela.
export const PRODUCT_CURRENCY_OPTIONS = [
  { code: 'BRL', flag: `${COUNTRY_FLAG_BASE_URL}/BR.svg`, label: 'Real brasileiro' },
  {
    code: 'USD',
    flag: `${COUNTRY_FLAG_BASE_URL}/US.svg`,
    label: 'Dólar americano',
    hint:
      'O preço é definido em USD para simplificar a configuração do produto, mas o valor final ' +
      'para os clientes será exibido na moeda local do país onde estiverem acessando.',
  },
];

export const DOLARIZATION_SUPPORTED_CURRENCIES = PRODUCT_CURRENCY_OPTIONS;
export const DASHBOARD_V2_SUPPORTED_CURRENCIES = DOLARIZATION_SUPPORTED_CURRENCIES;

const normalizeIsoCurrencyCode = (value) => {
  const upper = value != null && String(value).trim() !== '' ? String(value).trim().toUpperCase() : '';
  return /^[A-Z]{3}$/.test(upper) ? upper : '';
};

/**
 * Resolve moeda a partir de filtro de UI (ex.: CurrencyFilter).
 * "all" / ausente → fallback de formatação + flag de agregado misto.
 * @param {string} [filterValue]
 * @param {string} [fallbackCode='BRL']
 * @returns {{ currency: string, isMixedCurrency: boolean }}
 */
export function resolveFilterCurrency(
  filterValue,
  fallbackCode = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  const normalized = filterValue != null ? String(filterValue).trim().toLowerCase() : '';

  if (!normalized || normalized === ALL_CURRENCIES_FILTER_VALUE) {
    return {
      currency: normalizeIsoCurrencyCode(fallbackCode) || DEFAULT_PRODUCT_CURRENCY_CODE,
      isMixedCurrency: true,
    };
  }

  return {
    currency: normalizeIsoCurrencyCode(filterValue) || DEFAULT_PRODUCT_CURRENCY_CODE,
    isMixedCurrency: false,
  };
}

/**
 * Moeda enviada aos endpoints de relatório.
 * Sem feature multimoeda, omite o param na API (via ALL_CURRENCIES_FILTER_VALUE).
 */
export function resolveReportCurrencyForApi(currency, hasMultiCurrencyFeature) {
  return hasMultiCurrencyFeature ? currency : ALL_CURRENCIES_FILTER_VALUE;
}

/** Locale BCP 47 por ISO 4217  */
export const CURRENCY_LOCALE_MAP = {
  BRL: 'pt-BR',
  USD: 'en-US',
  // EUR: 'de-DE',
};

const DEFAULT_LOCALE_FOR_CURRENCY = 'pt-BR';

/**
 * @param {string} [code] — ISO 4217 (ex.: BRL, USD)
 * @returns {string}
 */
export function getLocaleForIsoCurrency(code) {
  const upper = code ? String(code).toUpperCase() : '';
  if (!upper) return DEFAULT_LOCALE_FOR_CURRENCY;
  return CURRENCY_LOCALE_MAP[upper] ?? DEFAULT_LOCALE_FOR_CURRENCY;
}

/**
 * Formata valor monetário para exibição (tabelas, resumos).
 *
 * Exige `currencyCode`: o default silencioso vivia aqui depois de sair do fCurrency, e
 * um valor em USD sem moeda declarada saía como R$ sem ninguém notar. Quando a moeda vem
 * de dado incompleto, resolva antes com resolveOrderCurrency / resolveOfferDisplayCurrency
 * / resolveCheckoutLinkCurrency — é neles que o fallback é uma decisão, não um acidente.
 *
 * @param {number|string} amount
 * @param {string} currencyCode — ISO 4217 (ex.: BRL, USD)
 * @param {Intl.NumberFormatOptions} [others] — ex.: casas decimais; não troca a moeda
 * @returns {string}
 * @throws {TypeError} quando currencyCode não é informado
 */
export function formatMoney(amount, currencyCode, others) {
  if (!currencyCode) {
    throw new TypeError('formatMoney requires a currency code');
  }

  const code = String(currencyCode).toUpperCase();
  return fCurrency(amount, code, getLocaleForIsoCurrency(code), others);
}

/**
 * Moeda para tooltip/gráfico de relatório: linha da API → escopo do filtro (CurrencyScopeProvider).
 * @param {object} [row]
 * @param {{ currency?: string } | null} [scope]
 * @returns {string} ISO 4217
 */
export function resolveReportRowDisplayCurrency(row, scope) {
  const rowCurrency = normalizeIsoCurrencyCode(row?.currency);
  if (rowCurrency) return rowCurrency;

  const scopeCurrency = normalizeIsoCurrencyCode(scope?.currency);
  if (scopeCurrency) return scopeCurrency;

  return DEFAULT_PRODUCT_CURRENCY_CODE;
}

export function getMarketplaceSalesThreshold(currencyCode = DEFAULT_PRODUCT_CURRENCY_CODE) {
  const code = (currencyCode || DEFAULT_PRODUCT_CURRENCY_CODE).toUpperCase();
  return MARKETPLACE_SALES_THRESHOLD_BY_CURRENCY[code] ?? MARKETPLACE_SALES_THRESHOLD_BY_CURRENCY.BRL;
}

/**
 * Moeda do link de checkout para renderização.
 * API: preferir `link.currency` ou `link.offer.currency` (ou
 * @param {object} [link]
 * @param {string} [productCurrencyFallback] — ex.: product.currency do GET produto
 * @returns {string} ISO 4217
 */
export function resolveCheckoutLinkCurrency(
  link,
  productCurrencyFallback = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  const pick = (v) => (v ? String(v).toUpperCase() : '');
  return (
    pick(link?.currency) ||
    pick(link?.offer?.currency) ||
    pick(productCurrencyFallback) ||
    DEFAULT_PRODUCT_CURRENCY_CODE
  );
}

/**
 * Moeda para listagem / formulário de oferta: registro da oferta → produto → padrão.
 * Aceita `offer` cru ou com `_raw` (lista / navegação).
 * @param {object} [offer]
 * @param {object} [product]
 * @param {string} [fallbackCode]
 * @returns {string} ISO 4217 em maiúsculas
 */
export function resolveOfferRowCurrency(
  offer,
  product,
  fallbackCode = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  const pick = (v) => {
    const s = v != null && String(v).trim() !== '' ? String(v).trim().toUpperCase() : '';
    return /^[A-Z]{3}$/.test(s) ? s : '';
  };
  const raw = offer?._raw ?? offer;
  return (
    pick(raw?.currency) ||
    pick(raw?.currency_code) ||
    pick(raw?.currencyCode) ||
    pick(product?.currency) ||
    pick(fallbackCode) ||
    DEFAULT_PRODUCT_CURRENCY_CODE
  );
}

/**
 * Moeda da oferta para exibição: ISO na oferta → fallback do produto (string) → BRL.
 * @param {object} [offer]
 * @param {string} [productCurrencyFallback] — ex.: product.currency do EditProductContext
 * @returns {string} ISO 4217 em maiúsculas
 */
export function resolveOfferDisplayCurrency(
  offer,
  productCurrencyFallback = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  return resolveOfferRowCurrency(offer, { currency: productCurrencyFallback });
}

/**
 * Moeda do item de pedido (GET /orders): raiz do pedido → product.currency → padrão.
 * @param {object} [order]
 * @param {string} [fallbackCode]
 * @returns {string} ISO 4217 em maiúsculas
 */
export function resolveOrderCurrency(order, fallbackCode = DEFAULT_PRODUCT_CURRENCY_CODE) {
  const pick = (v) => {
    const s = v != null && String(v).trim() !== '' ? String(v).trim().toUpperCase() : '';
    return /^[A-Z]{3}$/.test(s) ? s : '';
  };
  return (
    pick(order?.currency) ||
    pick(order?.currency_code) ||
    pick(order?.currencyCode) ||
    pick(order?.product?.currency) ||
    pick(fallbackCode) ||
    DEFAULT_PRODUCT_CURRENCY_CODE
  );
}

export function getCurrencyByCode(code) {
  if (!code) return null;

  return PRODUCT_CURRENCY_OPTIONS.find((currency) => currency.code === code.toUpperCase()) || null;
}

export function getProductCurrency(product) {
  return getCurrencyByCode(product?.currency) || getCurrencyByCode(DEFAULT_PRODUCT_CURRENCY_CODE);
}
